import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { node } from 'prop-types'

// Stable identifiers for the mutually-exclusive top-nav overlays. The action
// popovers (cart / inbox / notifications) use their backend action ids directly,
// so they are not listed here.
const NAV_OVERLAY_IDS = {
  HAMBURGER: 'hamburger',
  MENU: 'menu',
  USER_MENU: 'user-menu',
}

const NavOverlayContext = createContext(null)

// Single source of truth for which top-nav overlay is open, so only one of the
// hamburger menu, primary-menu dropdowns, action popovers and the user menu can
// be open at a time - opening one closes whatever else was open. Mounted once by
// Navigation around the whole bar.
function NavOverlayProvider({ children = null }) {
  const [openId, setOpenId] = useState(null)

  const open = useCallback((id) => setOpenId(id), [])

  // Guarded close: only clears the shared state when `id` is the one currently
  // open (or when no id is given). A stale close - e.g. a delayed hover-out that
  // fires after the pointer already opened a sibling - then cannot wipe the newer
  // overlay.
  const close = useCallback((id) => {
    setOpenId((current) => (id == null || current === id ? null : current))
  }, [])

  const value = useMemo(() => ({ openId, open, close }), [openId, open, close])

  return <NavOverlayContext.Provider value={value}>{children}</NavOverlayContext.Provider>
}

NavOverlayProvider.propTypes = {
  children: node,
}

// `id` is optional; pass it to derive `isOpen`. Outside the provider (e.g. a
// component mounted alone in a unit test) the hook falls back to isolated local
// state, so each overlay still opens and closes on its own - only the
// cross-overlay coordination is inert.
function useNavOverlay(id) {
  const shared = useContext(NavOverlayContext)
  const [localOpenId, setLocalOpenId] = useState(null)

  const openId = shared ? shared.openId : localOpenId

  const open = useCallback((nextId) => {
    if (shared) {
      shared.open(nextId)
    } else {
      setLocalOpenId(nextId)
    }
  }, [shared])

  const close = useCallback((closingId) => {
    if (shared) {
      shared.close(closingId)
    } else {
      setLocalOpenId((current) => (closingId == null || current === closingId ? null : current))
    }
  }, [shared])

  return {
    openId,
    isOpen: id != null && openId === id,
    open,
    close,
  }
}

export { NavOverlayProvider, useNavOverlay, NAV_OVERLAY_IDS }
